import React, { createContext, useReducer, ReactNode, useEffect, useRef } from 'react';
import { appStateReducer } from './AppReducer';
import { ChatHistoryLoadingState, CosmosDBHealth, historyList, historyEnsure, CosmosDBStatus } from '../api';
import { Conversation, frontendSettings, FrontendSettings, refreshfrontendSettings } from '../api';
import { PublicClientApplication } from "@azure/msal-browser";
import { initializeTeams, authenticateWithTeams, isTeamsAuthInProgress } from '../utils/teamsAuth';
import { setTeamsAccessToken } from '../utils/token';

// B2C Internal Auth State Types
export type B2CInternalAuthState =
    | 'not_applicable'    // B2C is not enabled for this app
    | 'admin_bypass'      // B2C is enabled but user has can_view_all_programs (bypasses internal program filtering)
    | 'loading'           // Authenticating or verifying groups
    | 'selecting_program' // User needs to select a program
    | 'pending'           // User has programGroup but no programRole
    | 'no_access'         // B2C user with no confirmed program assignment
    | 'wrong_url'         // User has a subscription elsewhere but not for this URL's programs
    | 'iframe_signin_required' // Non-Teams cross-origin iframe: need user gesture to open loginPopup
    | 'authenticated';    // User has both programGroup and programRole
  
export interface AppState {
    // showHideCitations: HideCitations;
    // showHideMsgFb: ShowMsgFb;
    isChatHistoryOpen: boolean;
    chatHistoryLoadingState: ChatHistoryLoadingState;
    isCosmosDBAvailable: CosmosDBHealth;
    chatHistory: Conversation[] | null;
    filteredChatHistory: Conversation[] | null;
    currentChat: Conversation | null;
    isFeedbackPanelOpen: boolean;
    feedbackPanelPayload: string;
    frontendSettings: FrontendSettings | null;
    isMsgFB: string;
    msalInstance: PublicClientApplication | null;
    b2cAuthResponse: any | null;
    msalInitialized: boolean;
    b2cInternalAuthState: B2CInternalAuthState;
    b2cInternalCheckComplete: boolean;
}

export type Action =
    // | { type: 'HIDE_CITATIONS', payload: HideCitations }
    // | { type: 'HIDE_MSG_FB', payload: ShowMsgFb }
    | { type: 'TOGGLE_CHAT_HISTORY' }
    | { type: 'SET_COSMOSDB_STATUS', payload: CosmosDBHealth }
    | { type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState }
    | { type: 'UPDATE_CURRENT_CHAT', payload: Conversation | null }
    | { type: 'UPDATE_FILTERED_CHAT_HISTORY', payload: Conversation[] | null }
    | { type: 'UPDATE_CHAT_HISTORY', payload: Conversation } // API Call
    | { type: 'UPDATE_CHAT_TITLE', payload: Conversation } // API Call
    | { type: 'DELETE_CHAT_ENTRY', payload: string } // API Call
    | { type: 'DELETE_CHAT_HISTORY'}  // API Call
    | { type: 'DELETE_CURRENT_CHAT_MESSAGES', payload: string }  // API Call
    | { type: 'FETCH_CHAT_HISTORY', payload: Conversation[] | null }  // API Call
    | { type: 'TOGGLE_FEEDBACK_PANEL' }
    | { type: 'CLOSE_FEEDBACK_PANEL' }
    | { type: 'FETCH_FRONTEND_SETTINGS', payload: FrontendSettings | null }  // API Call
    | { type: 'MSGFB', payload: string }
    | { type: 'ENV_UPDATE_FRONTEND_SETTINGS', payload: FrontendSettings | null }  // API Call
    | { type: 'SET_B2C_AUTH_RESPONSE', payload: { instance: PublicClientApplication, response: any } }
    | { type: 'SET_B2C_INTERNAL_AUTH_STATE', payload: B2CInternalAuthState }
    | { type: 'SET_B2C_INTERNAL_CHECK_COMPLETE', payload: boolean }

const initialState: AppState = {
    // showHideCitations:  {
    //     hideCitations: "false"
    // },
    // showHideMsgFb:  {
    //     showMsgFb: "false"
    // },
    isChatHistoryOpen: false,
    chatHistoryLoadingState: ChatHistoryLoadingState.Loading,
    chatHistory: null,
    filteredChatHistory: null,
    currentChat: null,
    isCosmosDBAvailable: {
        cosmosDB: false,
        status: CosmosDBStatus.NotConfigured,
        allowMessageDelete: "false"
    },
    isFeedbackPanelOpen: false,
    feedbackPanelPayload: '',
    frontendSettings: null,
    isMsgFB:"N",
    msalInstance: null,
    b2cAuthResponse: null,
    msalInitialized: false,
    b2cInternalAuthState: 'not_applicable',
    b2cInternalCheckComplete: false,
};

export const AppStateContext = createContext<{
    state: AppState;
    dispatch: React.Dispatch<Action>;
    completeB2CAuthFlow: (msalInstance: PublicClientApplication, authResponse: any) => Promise<void>;
  } | undefined>(undefined);

type AppStateProviderProps = {
    children: ReactNode;
  };
  
  export const AppStateProvider: React.FC<AppStateProviderProps> = ({ children }) => {
    const [state, dispatch] = useReducer(appStateReducer, initialState);
    const msalInitInProgressRef = useRef(false);

    useEffect(() => {
        // Check for cosmosdb config and fetch initial data here
        // const getshowhideMsgFb = async () => {
        //     gethideMsgFb().then((response) => {
               
        //         if(response?.showMsgFb){
        //             dispatch({ type: 'HIDE_MSG_FB', payload: response });
        //         }
        //         else {
        //             dispatch({ type: 'HIDE_MSG_FB', payload: { showMsgFb: "false"} });
        //         }
            
        //     })
        //     .catch((err) => {
        //         dispatch({ type: 'HIDE_MSG_FB', payload: { showMsgFb: "false"} });
        //     })
        // };
        // const getshowhideCitations = async () => {
        //     gethideCitations().then((response) => {
               
        //         if(response?.hideCitations){
        //             dispatch({ type: 'HIDE_CITATIONS', payload: response });
        //         }
        //         else {
        //             dispatch({ type: 'HIDE_CITATIONS', payload: { hideCitations: "false"} });
        //         }
            
        //     })
        //     .catch((err) => {
        //         dispatch({ type: 'HIDE_CITATIONS', payload: { hideCitations: "false"} });
        //     })
        // };

        const fetchChatHistory = async (offset=0): Promise<Conversation[] | null> => {
            const result = await historyList(offset).then((response) => {
                if(response){
                    dispatch({ type: 'FETCH_CHAT_HISTORY', payload: response });
                }else{
                    dispatch({ type: 'FETCH_CHAT_HISTORY', payload: null });
                }
                return response
            })
            .catch((err) => {
                dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Fail });
                dispatch({ type: 'FETCH_CHAT_HISTORY', payload: null });
                console.error("There was an issue fetching your data.");
                return null
            })
            return result
        };

        const getHistoryEnsure = async () => {
            dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Loading });
            historyEnsure().then((response) => {
                if(response?.cosmosDB){
                    fetchChatHistory()
                    .then((res) => {
                        if(res){
                            dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Success });
                            dispatch({ type: 'SET_COSMOSDB_STATUS', payload: response });
                        }else{
                            dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Fail });
                            dispatch({ type: 'SET_COSMOSDB_STATUS', payload: {cosmosDB: false, status: CosmosDBStatus.NotWorking, allowMessageDelete: "false"} });
                        }
                    })
                    .catch((err) => {
                        dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Fail });
                        dispatch({ type: 'SET_COSMOSDB_STATUS', payload: {cosmosDB: false, status: CosmosDBStatus.NotWorking, allowMessageDelete: "false"} });
                    })
                }else{
                    dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Fail });
                    dispatch({ type: 'SET_COSMOSDB_STATUS', payload: response });
                }
            })
            .catch((err) => {
                dispatch({ type: 'UPDATE_CHAT_HISTORY_LOADING_STATE', payload: ChatHistoryLoadingState.Fail });
                dispatch({ type: 'SET_COSMOSDB_STATUS', payload: {cosmosDB: false, status: CosmosDBStatus.NotConfigured, allowMessageDelete: "false"} });
            })
        }
        getHistoryEnsure();
    }, []);

    // Phase 1: Fetch frontend settings (no token on first load)
    useEffect(() => {
      const fetchInitialSettings = async () => {
        try {
          const storedProgramConfig = sessionStorage.getItem('selectedProgramConfig');
          const storedProgramCode = sessionStorage.getItem('selectedProgramCode');
          const msal = null; // No MSAL instance yet during initial load
          const settings = null; // Settings info not available yet during initial load

          const response = await frontendSettings(msal, settings) as FrontendSettings;
          const useB2C = !!response?.branding?.YW_USE_AZURE_B2C;

          if (storedProgramConfig && !useB2C) {
            const refreshedResponse = await refreshfrontendSettings(storedProgramConfig, document.documentElement.lang, storedProgramCode, msal, settings) as FrontendSettings;
            dispatch({ type: 'ENV_UPDATE_FRONTEND_SETTINGS', payload: refreshedResponse });
          } else {
            dispatch({ type: 'FETCH_FRONTEND_SETTINGS', payload: response });
          }
        } catch (error) {
          console.error("Error fetching frontend settings:", error);
        }
      }
      fetchInitialSettings();
    }, []);

    // --- B2C Auth Helpers ---

    // Attempt to get an auth response via: redirect promise → silent acquisition → interactive login
    const resolveAuthResponse = async (
      msalInstance: PublicClientApplication,
      loginRequest: { scopes: string[]; prompt: string },
      options: { isInIframe: boolean; useB2C: boolean; passwordResetAuthority?: string; hasInternalPrograms: boolean }
    ): Promise<any | null> => {
      let authResponse = null;

      // 1. Check for redirect response (not available in iframes)
      if (!options.isInIframe) {
        try {
          authResponse = await msalInstance.handleRedirectPromise();
        } catch (redirectError: any) {
          console.error("[resolveAuth] handleRedirectPromise error:", redirectError);
          if (options.useB2C) {
            // AADB2C90118 = user clicked "Forgot password" on the B2C login page
            if (options.passwordResetAuthority && redirectError?.errorMessage?.includes("AADB2C90118")) {
              await msalInstance.loginRedirect({ ...loginRequest, authority: options.passwordResetAuthority });
              return null;
            }
            // AADB2C90091 = user canceled the login (e.g. closed popup or hit back on redirect)
            if (redirectError?.errorMessage?.includes("AADB2C90091")) {
              await msalInstance.loginRedirect(loginRequest);
              return null;
            }
          }
          throw redirectError;
        }
      }

      if (authResponse) return authResponse;

      // 2. Try silent token acquisition from cached account
      const cachedAccounts = msalInstance.getAllAccounts();
      if (cachedAccounts.length > 0) {
        try {
          authResponse = await msalInstance.acquireTokenSilent({
            scopes: loginRequest.scopes,
            account: cachedAccounts[0],
          });
        } catch (silentError) {
          console.warn("Silent token acquisition failed, falling back to interactive login:", silentError);
        }
      }

      if (authResponse) return authResponse;

      // 3. Fall back to interactive login (internal programs only)
      if (options.hasInternalPrograms) {
        if (options.isInIframe) {
          // Cross-origin iframe: browsers block popups without a user gesture.
          // Signal the caller to render a sign-in button; the click handler will call loginPopup.
          return null;
        } else {
          await msalInstance.loginRedirect(loginRequest);
          return null; // Page will reload after redirect
        }
      }

      return null;
    };

    // Run the shared post-auth steps for both Teams and non-Teams flows:
    // set the active account, re-fetch settings with token, and dispatch resulting state.
    const completeB2CAuthFlow = async (
      msalInstance: PublicClientApplication,
      authResponse: any
    ): Promise<void> => {
      if (authResponse?.account) {
        msalInstance.setActiveAccount(authResponse.account);
      }

      const settings = state.frontendSettings;
      const hasInternalPrograms = !!settings?.has_internal_programs;

      if (hasInternalPrograms) {
        dispatch({ type: 'SET_B2C_INTERNAL_AUTH_STATE', payload: 'loading' });
        try {
          const authState = await fetchAuthenticatedSettings(msalInstance, settings);
          dispatch({ type: 'SET_B2C_INTERNAL_AUTH_STATE', payload: authState });
        } catch (err) {
          console.error("Error re-fetching settings with token:", err);
          dispatch({ type: 'SET_B2C_INTERNAL_AUTH_STATE', payload: 'no_access' });
        }
      } else {
        // External-only: re-fetch settings with token so server can pick up the
        // previously selected program from session storage, if any.
        try {
          const storedProgramConfig = sessionStorage.getItem('selectedProgramConfig');
          const storedProgramCode = sessionStorage.getItem('selectedProgramCode');
          const response: FrontendSettings | null = storedProgramConfig
            ? await refreshfrontendSettings(storedProgramConfig, document.documentElement.lang, storedProgramCode, msalInstance, settings) as FrontendSettings
            : await frontendSettings(msalInstance, settings) as FrontendSettings;
          if (response) {
            dispatch({ type: 'FETCH_FRONTEND_SETTINGS', payload: response });
          }
        } catch (err) {
          console.error("Error re-fetching external settings with token:", err);
        }
        dispatch({ type: 'SET_B2C_INTERNAL_AUTH_STATE', payload: 'not_applicable' });
      }

      dispatch({ type: 'SET_B2C_INTERNAL_CHECK_COMPLETE', payload: true });
      dispatch({ type: 'SET_B2C_AUTH_RESPONSE', payload: { instance: msalInstance, response: authResponse } });
    };

    // Re-fetch settings with token and determine B2C internal auth state
    const fetchAuthenticatedSettings = async (
      msalInstance: PublicClientApplication,
      settings: any
    ): Promise<B2CInternalAuthState> => {
      const response = await frontendSettings(msalInstance, settings) as FrontendSettings;

      if (!response) {
        console.warn("Failed to re-fetch settings after B2C auth, blocking access.");
        return 'no_access';
      }

      dispatch({ type: 'FETCH_FRONTEND_SETTINGS', payload: response });

      if (response.can_view_all_programs) return 'admin_bypass';
      if (response.is_wrong_url) return 'wrong_url';
      if (response.is_pending_access) return 'pending';
      if (response.filtered_internal_programs?.length) return 'selecting_program';
      if (response.matched_program_name) return 'authenticated';

      console.warn("B2C re-fetch returned no program assignment info. User may not have proper access.");
      return 'no_access';
    };

    // Phase 2: Initialize MSAL and handle B2C auth + settings re-fetch
    useEffect(() => {
      const isAuthRoute = window.location.pathname === '/auth-start' || window.location.pathname === '/auth-end';

      const settings = state.frontendSettings;
      // Wait for settings to load before doing anything — without them we can't tell
      // whether B2C is configured. The effect re-runs when settings land.
      if (!settings?.branding) return;

      const azure_b2c_client_id = settings.branding.azure_b2c_client_id;
      const azure_b2c_userflow_endpoint = settings.branding.azure_b2c_userflow_endpoint;
      const azure_b2c_profileflow_endpoint = settings.branding.azure_b2c_profileflow_endpoint;
      const azure_b2c_redirect_uri = settings.branding.azure_b2c_redirect_uri;
      const useB2C = settings.branding.YW_USE_AZURE_B2C;

      // B2C not in use — mark as not applicable and stop.
      if (!useB2C) {
        dispatch({ type: 'SET_B2C_INTERNAL_AUTH_STATE', payload: 'not_applicable' });
        dispatch({ type: 'SET_B2C_INTERNAL_CHECK_COMPLETE', payload: true });
        return;
      }

      // B2C enabled but config is incomplete — surface as no_access.
      if (!azure_b2c_client_id || !azure_b2c_userflow_endpoint || !azure_b2c_profileflow_endpoint || !azure_b2c_redirect_uri) {
        console.warn("B2C is enabled but MSAL config is incomplete (missing client_id, userflow, profileflow, or redirect_uri). Cannot initialize B2C auth.");
        dispatch({ type: 'SET_B2C_INTERNAL_AUTH_STATE', payload: 'no_access' });
        dispatch({ type: 'SET_B2C_INTERNAL_CHECK_COMPLETE', payload: true });
        return;
      }

      // MSAL already initialized (either from a prior run, or initialization is
      // currently in flight). Skip — completeB2CAuthFlow will dispatch when it lands.
      if (state.msalInstance || msalInitInProgressRef.current || isAuthRoute) return;

      const initAuth = async () => {
          msalInitInProgressRef.current = true;
          try {
            const inTeams = await initializeTeams();

            // Skip if Teams auth is already in progress (prevents duplicate popups)
            if (inTeams && isTeamsAuthInProgress()) return;

            const msalConfig = {
                auth: {
                    clientId: azure_b2c_client_id,
                    authority: azure_b2c_userflow_endpoint,
                    redirectUri: azure_b2c_redirect_uri,
                    //redirectUri: 'http://localhost:50505/', // for testing locally
                    knownAuthorities: [
                      new URL(azure_b2c_userflow_endpoint).hostname,
                      new URL(azure_b2c_profileflow_endpoint).hostname
                    ],
                },
                cache: {
                    cacheLocation: "localStorage",
                    storeAuthStateInCookie: false,
                },
                system: { allowRedirectInIframe: true }
            };

            const msalInstance = new PublicClientApplication(msalConfig);
            await msalInstance.initialize();

            let isInIframe = false;
            try { isInIframe = window.self !== window.top; } catch (e) { isInIframe = true; }

            // Must include the API scope during login so B2C issues an access token.
            // Without it, acquireTokenSilent returns an empty accessToken.
            const b2cTenantName = settings.branding?.b2c_tenant_subdomain || "";
            const apiScope = b2cTenantName
              ? `https://${b2cTenantName}.onmicrosoft.com/${azure_b2c_client_id}/access_as_user`
              : `api://${azure_b2c_client_id}/access_as_user`;

            const loginRequest = { scopes: [apiScope], prompt: "select_account" };
            const hasInternalPrograms = !!settings.has_internal_programs;
            const passwordResetAuthority = settings.branding?.azure_b2c_password_reset_endpoint;

            let authResponse: any = null;

            if (inTeams && hasInternalPrograms) {
              // Teams popup flow: acquire auth via Teams-managed popup, store wrapped token.
              const teamsResult = await authenticateWithTeams(azure_b2c_client_id, azure_b2c_userflow_endpoint, apiScope);
              authResponse = {
                ...teamsResult,
                idTokenClaims: teamsResult.account?.idTokenClaims,
              };
              if (!teamsResult.encryptedToken) {
                throw new Error("No encrypted token available for Teams auth");
              }
              const expiration = teamsResult.accessTokenExpiry ?? undefined;
              setTeamsAccessToken(teamsResult.encryptedToken, expiration);
            } else {
              // Non-Teams: standard redirect/silent flow (loginPopup is gated to a click handler).
              authResponse = await resolveAuthResponse(msalInstance, loginRequest, {
                isInIframe, useB2C: true, passwordResetAuthority, hasInternalPrograms,
              });

              // No auth and no internal programs — external-only, user can sign in manually
              if (!authResponse && !hasInternalPrograms) {
                dispatch({ type: 'SET_B2C_AUTH_RESPONSE', payload: { instance: msalInstance, response: null } });
                dispatch({ type: 'SET_B2C_INTERNAL_AUTH_STATE', payload: 'not_applicable' });
                dispatch({ type: 'SET_B2C_INTERNAL_CHECK_COMPLETE', payload: true });
                return;
              }

              // No auth response in a cross-origin iframe — silent acquisition failed and
              // browsers block popups without a user gesture. Surface the sign-in gate.
              if (!authResponse && isInIframe && hasInternalPrograms) {
                dispatch({ type: 'SET_B2C_AUTH_RESPONSE', payload: { instance: msalInstance, response: null } });
                dispatch({ type: 'SET_B2C_INTERNAL_AUTH_STATE', payload: 'iframe_signin_required' });
                dispatch({ type: 'SET_B2C_INTERNAL_CHECK_COMPLETE', payload: true });
                return;
              }

              // No auth response (redirect in progress) — exit and wait for page reload
              if (!authResponse) return;
            }

            // Shared post-auth flow (Teams and non-Teams): set active account,
            // re-fetch settings with token, and dispatch resulting state.
            await completeB2CAuthFlow(msalInstance, authResponse);
          } catch (err) {
            console.error("Error in initAuth:", err);
            dispatch({ type: 'SET_B2C_INTERNAL_AUTH_STATE', payload: 'no_access' });
            dispatch({ type: 'SET_B2C_INTERNAL_CHECK_COMPLETE', payload: true });
          } finally {
            msalInitInProgressRef.current = false;
          }
      }
      initAuth();
   }, [state.frontendSettings, state.msalInstance]);

    return (
      <AppStateContext.Provider value={{ state, dispatch, completeB2CAuthFlow }}>
        {children}
      </AppStateContext.Provider>
    );
  };

